import React, { useState } from "react";
import { Mail, FileText, Shield, RefreshCcw, MessageCircle, Send } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";

const LegalLayout = ({ icon: Icon, title, subtitle, children }) => (
  <div className="max-w-3xl mx-auto px-6 py-14">
    <div className="flex items-center gap-3 mb-2">
      <div className="w-10 h-10 rounded-xl bg-indigo-100 text-indigo-700 flex items-center justify-center">
        <Icon className="w-5 h-5" />
      </div>
      <div>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold">{title}</h1>
        <div className="text-xs text-slate-500 uppercase tracking-wider font-bold mt-1">{subtitle}</div>
      </div>
    </div>
    <div className="prose prose-slate max-w-none mt-8 space-y-5 text-slate-700 leading-relaxed">
      {children}
    </div>
  </div>
);

const H2 = ({ children }) => <h2 className="font-display text-xl font-extrabold text-slate-900 mt-8 mb-2">{children}</h2>;

export const Terms = () => (
  <LegalLayout icon={FileText} title="Terms of Service" subtitle="Last updated 29 Feb 2026">
    <p>Welcome to SupplySprout. By using our site, placing an order or creating an account, you agree to these terms. If you don't agree, please don't use the site.</p>

    <H2>1. Who we are</H2>
    <p>SupplySprout ("we", "us") is a UK-based online stationery retailer. Our registered contact email is support@supplysprout.co.uk. Replace this placeholder with your real trading name and address before going live.</p>

    <H2>2. Ordering & pricing</H2>
    <p>All prices are shown in GBP (or your displayed currency) and include VAT where applicable. We may change prices at any time, but a confirmed order will be fulfilled at the price shown at checkout. We reserve the right to cancel an order if stock, pricing or fulfilment problems arise, in which case we refund you in full.</p>

    <H2>3. Payment</H2>
    <p>Payment is taken securely by Stripe. We never see your card details. By completing checkout you authorise Stripe to charge your card for the full order total.</p>

    <H2>4. Shipping & fulfilment</H2>
    <p>Most orders ship from our wholesale partner within 24 hours of payment. Estimated delivery is 7-12 working days to the UK, longer to other destinations (shown at checkout). We don't guarantee delivery dates but we do email you tracking status at every stage.</p>

    <H2>5. Teacher discount</H2>
    <p>Verified teachers receive 15% off retail prices automatically. We may revoke verification if we have good reason to believe the claim is false.</p>

    <H2>6. Returns</H2>
    <p>See our Returns & Refunds policy for your 14-day right to cancel under UK consumer law.</p>

    <H2>7. Account use</H2>
    <p>Keep your password secret. You are responsible for activity on your account. Please notify us immediately if you suspect unauthorised use.</p>

    <H2>8. Liability</H2>
    <p>Nothing here limits our liability for fraud, death or personal injury caused by our negligence, or for anything that cannot be excluded under English law. Otherwise, our maximum liability for any order is the amount you paid for that order.</p>

    <H2>9. Governing law</H2>
    <p>These terms are governed by the law of England and Wales. Disputes go to the courts of England and Wales.</p>

    <p className="text-xs text-slate-500 mt-10">⚠️ This is a sensible starter template. Have it reviewed by a solicitor before trading seriously, especially the liability, tax and governing-law clauses.</p>
  </LegalLayout>
);

export const Privacy = () => (
  <LegalLayout icon={Shield} title="Privacy Policy" subtitle="Last updated 29 Feb 2026">
    <p>We only collect the data we need to run the shop and we never sell it. This policy explains what we hold, why, and how to control it.</p>

    <H2>What we collect</H2>
    <ul className="list-disc pl-5 space-y-1">
      <li>Account info — name, email, hashed password (bcrypt), role (parent/teacher)</li>
      <li>Order info — items, totals, shipping address, order history</li>
      <li>Teacher verification — school name, school email, grade/subject (only if you apply)</li>
      <li>Payment — handled by Stripe; we only receive a confirmation and the last 4 digits</li>
      <li>Usage logs — session cookies and request logs for security</li>
    </ul>

    <H2>Why we hold it</H2>
    <p>To ship your order, send you order updates, apply your teacher discount, respond to support requests, and prevent fraud.</p>

    <H2>Who we share it with</H2>
    <ul className="list-disc pl-5 space-y-1">
      <li>Stripe — card processing</li>
      <li>CJ Dropshipping — your shipping address, so your order can be sent to you</li>
      <li>Resend — to deliver order confirmation and status emails</li>
      <li>HM Revenue & Customs or authorities if legally required</li>
    </ul>
    <p>That's it. No marketing resale, ever.</p>

    <H2>Your rights (UK/EU GDPR)</H2>
    <p>You can ask us at any time to: see what we hold about you, correct it, delete it, export it, or stop processing it. Email support@supplysprout.co.uk and we'll respond within 30 days.</p>

    <H2>Cookies</H2>
    <p>We use essential cookies for login sessions and cart state. We don't run advertising trackers. Analytics (if added later) will be opt-in.</p>

    <H2>Retention</H2>
    <p>Orders and invoices are kept 7 years (HMRC requirement). Accounts are deleted on request. Chat transcripts with the AI assistant are kept 90 days.</p>

    <p className="text-xs text-slate-500 mt-10">⚠️ Replace "SupplySprout" and the support email with your real trading identity before launch, and register as a data controller with the ICO if you're based in the UK.</p>
  </LegalLayout>
);

export const Refund = () => (
  <LegalLayout icon={RefreshCcw} title="Returns & Refunds" subtitle="Your 14-day right to cancel">
    <p>We want you happy with every order. Here's the short version — contact us and we'll make it right.</p>

    <H2>14-day cooling-off</H2>
    <p>Under UK Consumer Contracts Regulations, you can cancel any online order up to 14 days after receiving it, no reason needed. Email support@supplysprout.co.uk to start the process. Items must be unused and in resellable condition.</p>

    <H2>How to return</H2>
    <ol className="list-decimal pl-5 space-y-1">
      <li>Email us within 14 days of delivery with your order number.</li>
      <li>We'll reply with a prepaid return label.</li>
      <li>Drop the parcel off at any Royal Mail post office.</li>
      <li>Once we receive it, we refund within 5 working days.</li>
    </ol>

    <H2>Faulty or damaged items</H2>
    <p>If something arrives broken or doesn't match the description, you have 30 days under the Consumer Rights Act 2015 to a full refund — no restocking fee. We'll cover the return shipping.</p>

    <H2>What we can't refund</H2>
    <ul className="list-disc pl-5 space-y-1">
      <li>Personalised or custom-printed items</li>
      <li>Items damaged by misuse after delivery</li>
      <li>Classroom bulk orders over £500 — these are covered by a separate written agreement</li>
    </ul>

    <H2>Wholesale orders</H2>
    <p>Schools and bulk buyers: terms are governed by your signed quote. Contact your account manager if you need to amend an order.</p>
  </LegalLayout>
);

export const Contact = () => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");

  const submit = (e) => {
    e.preventDefault();
    const body = encodeURIComponent(`${message}\n\n— ${name} (${email})`);
    const mailto = `mailto:support@supplysprout.co.uk?subject=${encodeURIComponent(subject || "SupplySprout enquiry")}&body=${body}`;
    window.location.href = mailto;
    toast.success("Opening your email app…");
  };

  return (
    <LegalLayout icon={MessageCircle} title="Contact us" subtitle="We reply within 1 working day">
      <p>Order questions, teacher verification, bulk quotes, press — email us at <a className="text-indigo-600 underline" href="mailto:support@supplysprout.co.uk">support@supplysprout.co.uk</a> or use the form below.</p>

      <div className="not-prose mt-6 p-6 bg-white rounded-2xl border border-slate-200">
        <form onSubmit={submit} className="space-y-4" data-testid="contact-form">
          <div className="grid sm:grid-cols-2 gap-3">
            <Input placeholder="Your name" value={name} onChange={(e) => setName(e.target.value)} required data-testid="contact-name" />
            <Input type="email" placeholder="Your email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="contact-email" />
          </div>
          <Input placeholder="Subject" value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="contact-subject" />
          <Textarea placeholder="How can we help?" rows={5} value={message} onChange={(e) => setMessage(e.target.value)} required data-testid="contact-message" />
          <Button type="submit" className="w-full rounded-full bg-indigo-600 hover:bg-indigo-700" data-testid="contact-submit">
            <Send className="w-4 h-4 mr-1" />Send message
          </Button>
        </form>
        <div className="mt-4 text-xs text-slate-500">We never share your details. See <a className="text-indigo-600 underline" href="/privacy">Privacy policy</a>.</div>
      </div>

      <div className="mt-8 p-5 rounded-2xl bg-indigo-50 border border-indigo-100">
        <div className="font-semibold flex items-center gap-2"><Mail className="w-4 h-4 text-indigo-600" />Teacher support</div>
        <div className="text-sm text-slate-700 mt-1">Verified teachers get priority replies and a dedicated account manager for bulk orders over £500.</div>
      </div>
    </LegalLayout>
  );
};
